/**
 * Types for the Business Support > Take Backup surface.
 *
 * Mirrors the backend DTOs in `com.reverse.job.portal.Backup.DTO`.
 */

export type BackupEnvironment = 'PROD' | 'UAT' | 'STAGE' | 'LOCAL' | 'CUSTOM';

/** Which source a run read from; also which Take Backup tab owns it. */
export type BackupSource = 'DB' | 'JENKINS' | 'MAILHOG';

export const BACKUP_SOURCE_LABELS: Record<BackupSource, string> = {
  DB: 'Database',
  JENKINS: 'Jenkins',
  MAILHOG: 'MailHog',
};

/**
 * What a run produces. CSV and Excel write one file per table; the SQL_* options each
 * write one `dump/<db>.sql` whose contents differ:
 *
 * - `SQL_DATA_ONLY`      INSERTs only - load into a fresh Flyway-managed database.
 * - `SQL_FULL_DATABASE`  CREATE DATABASE + tables + rows - full recovery dump.
 * - `SQL_DATA_SCHEMA_OBJECTS` schema + data + views/routines/triggers - portable export.
 * - `SQL`                legacy alias of the full database dump.
 */
export type BackupFormat =
  | 'CSV'
  | 'EXCEL'
  | 'SQL'
  | 'SQL_DATA_ONLY'
  | 'SQL_FULL_DATABASE'
  | 'SQL_DATA_SCHEMA_OBJECTS';

/** The three SQL dump variants, as stored on the job row. */
export type SqlBackupType = 'DATA_ONLY' | 'FULL_DATABASE' | 'DATA_SCHEMA_OBJECTS';

export type BackupStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';

export interface BackupConfig {
  id: string;
  name: string;
  environment: BackupEnvironment;
  host: string;
  port: number;
  databaseName: string;
  username: string;
  baseFolderPath: string;
  active: boolean;
  /** A password is stored but is never returned. */
  passwordSet: boolean;
  /** Drives whether Take Backup is enabled. */
  folderExists: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface BackupConfigRequest {
  name: string;
  environment: string;
  host: string;
  port?: number;
  databaseName: string;
  username: string;
  /** Blank on update keeps the stored password. */
  password?: string;
  baseFolderPath: string;
  active?: boolean;
}

export interface BackupJob {
  id: string;
  /** DB | JENKINS | MAILHOG - which source produced this run. */
  backupType?: BackupSource | null;
  /** Jenkins/MailHog config that owned the run; null for database runs. */
  configId?: string | null;
  dbConfigId: string | null;
  dbName: string | null;
  environment: BackupEnvironment | null;
  folderPath: string | null;
  formats: BackupFormat[] | null;
  status: BackupStatus;
  tableCount: number | null;
  processedTableCount: number | null;
  totalRows: number | null;
  processedRows: number | null;
  /** 0-99.9 while running, drives the percentage bar. */
  progressPercentage: number | null;
  /** Remaining time extrapolated from the observed rate; null until rows are flowing. */
  estimatedTimeMs: number | null;
  elapsedTimeMs: number | null;
  sizeBytes: number | null;
  errorMessage: string | null;
  createdBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  /** Per-table lines: "Exported table: <name> (<n> rows)". */
  auditLogs: string[] | null;
  cancelled: boolean | null;
}

export interface FolderStatus {
  path: string | null;
  exists: boolean;
  writable: boolean;
  message: string;
}

export interface TakeBackupRequest {
  dbConfigId: string;
  formats: BackupFormat[];
  label?: string;
  /** Empty/omitted exports every table. */
  selectedTables?: string[];
  sqlBackupType?: SqlBackupType;
}

export interface BackupJobFilters {
  backupType?: string;
  environment?: string;
  status?: string;
  format?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
}

export const BACKUP_ENVIRONMENTS: BackupEnvironment[] = ['PROD', 'UAT', 'STAGE', 'LOCAL', 'CUSTOM'];

export const BACKUP_ENV_LABELS: Record<BackupEnvironment, string> = {
  PROD: 'Production',
  UAT: 'UAT',
  STAGE: 'Staging',
  LOCAL: 'Local',
  CUSTOM: 'Custom',
};

export const BACKUP_FORMATS: BackupFormat[] = [
  'CSV',
  'EXCEL',
  'SQL_DATA_ONLY',
  'SQL_FULL_DATABASE',
  'SQL_DATA_SCHEMA_OBJECTS',
];

export const BACKUP_FORMAT_LABELS: Record<BackupFormat, string> = {
  CSV: 'CSV',
  EXCEL: 'Excel (.xlsx)',
  SQL: 'Whole DB (SQL dump)',
  SQL_DATA_ONLY: 'SQL — Data Only',
  SQL_FULL_DATABASE: 'SQL — Full Database',
  SQL_DATA_SCHEMA_OBJECTS: 'SQL — Data + Schema + Objects',
};

export const BACKUP_FORMAT_HELP: Record<BackupFormat, string> = {
  CSV: 'One .csv per table',
  EXCEL: 'One .xlsx per table',
  SQL: 'CREATE TABLE + INSERTs for the whole database',
  SQL_DATA_ONLY: 'INSERTs only — migrate into a fresh Flyway-managed database',
  SQL_FULL_DATABASE: 'Recovery dump — recreates the database, tables and rows',
  SQL_DATA_SCHEMA_OBJECTS: 'Portable — schema, data, views, routines and triggers',
};

export const SQL_FORMATS: BackupFormat[] = [
  'SQL_DATA_ONLY',
  'SQL_FULL_DATABASE',
  'SQL_DATA_SCHEMA_OBJECTS',
];

export const isSqlFormat = (format: BackupFormat): boolean => SQL_FORMATS.includes(format);

/** Maps a selected SQL format to the value stored on the job row. */
export const sqlTypeOfFormat = (format: BackupFormat): SqlBackupType | null => {
  switch (format) {
    case 'SQL_DATA_ONLY':
      return 'DATA_ONLY';
    case 'SQL_DATA_SCHEMA_OBJECTS':
      return 'DATA_SCHEMA_OBJECTS';
    case 'SQL':
    case 'SQL_FULL_DATABASE':
      return 'FULL_DATABASE';
    default:
      return null;
  }
};

export const BACKUP_STATUSES: BackupStatus[] = [
  'PENDING',
  'RUNNING',
  'SUCCESS',
  'FAILED',
  'CANCELLED',
];

export const BACKUP_STATUS_LABELS: Record<BackupStatus, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  SUCCESS: 'Success',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

// ------------------------------------------------------------------
// Jenkins source
// ------------------------------------------------------------------

export interface JenkinsConfig {
  id: string;
  name: string;
  baseUrl: string;
  username: string;
  /** Never returned; true when a token is stored. */
  tokenSet: boolean;
  baseFolderPath: string;
  active: boolean;
  folderExists: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface JenkinsConfigRequest {
  name: string;
  baseUrl: string;
  username: string;
  /** Blank/omitted on update keeps the stored token. */
  apiToken?: string;
  baseFolderPath: string;
  active?: boolean;
}

// ------------------------------------------------------------------
// MailHog source
// ------------------------------------------------------------------

export interface MailHogConfig {
  id: string;
  name: string;
  apiBaseUrl: string;
  /** Optional Basic-auth user for a proxy in front of MailHog. */
  username: string | null;
  /** Never returned; true when a password is stored. */
  passwordSet: boolean;

  /** Optional SSH host for server-side config capture. */
  sshHost: string | null;
  sshPort: number | null;
  sshUsername: string | null;
  sshPasswordSet: boolean;
  sshKeySet: boolean;

  baseFolderPath: string;
  active: boolean;
  folderExists: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface MailHogConfigRequest {
  name: string;
  apiBaseUrl: string;
  /** Blank clears the stored credentials. */
  username?: string;
  /** Blank on update keeps the stored password. */
  password?: string;

  /** Optional SSH settings for pulling server-side deployment files. */
  sshHost?: string;
  sshPort?: number;
  sshUsername?: string;
  /** Blank on update keeps the stored SSH password. */
  sshPassword?: string;
  /** Blank on update keeps the stored private key. */
  sshPrivateKey?: string;
  /** Blank on update keeps the stored key passphrase. */
  sshKeyPassphrase?: string;

  baseFolderPath: string;
  active?: boolean;
}
